/* Source unique du parcours d'onboarding.

   - SOCLE : les 5 tutos de base, dans un ORDRE FIXE (commun à tous).
   - PRIORITY_TO_TOUR : mapping « carte priorité » (choisie au signup) → tuto.
   - buildJourney() : compose la liste finale = SOCLE (ordre fixe) suivi des
     priorités choisies, DANS L'ORDRE DE CLIC de l'utilisateur.

   L'ordre du socle vient d'ici ; l'ordre des priorités vient de la sélection
   utilisateur (donc du back, qui doit conserver l'ordre de l'upsert). */

export interface JourneyStep {
  /** id du tuto enregistré via FygrTour.register() */
  id: string;
  label: string;
  page: string;
}

// SOCLE — ordre fixe, ne dépend pas de l'utilisateur.
export const SOCLE_STEPS: JourneyStep[] = [
  {id: 'dashboard-welcome', label: 'Découvrir le tableau de bord', page: '/dashboard'},
  {id: 'forecast-objectives', label: 'Saisir un objectif à la main', page: '/dashboard'},
  {id: 'forecast-formulas', label: 'Construire votre prévisionnel', page: '/dashboard'},
  {id: 'forecast-ai', label: 'Laisser l’IA proposer une prévision', page: '/dashboard'},
  {id: 'categorization-overview', label: 'Catégoriser vos transactions', page: '/dashboard'},
];

// Carte priorité (sa `key`, cf. CURATED_PRIORITIES dans OnboardingPriorities)
// → tuto débloqué et ajouté au parcours si la carte est choisie.
export const PRIORITY_TO_TOUR: Record<string, JourneyStep> = {
  invoices: {id: 'invoices-overview', label: 'Découvrir les échéances', page: '/invoices'},
  scenarios: {id: 'scenarios', label: 'Créer un scénario', page: '/dashboard'},
  consolidate: {id: 'entities-consolidation-onboarding', label: 'Consolider vos entités', page: '/dashboard'},
  daily: {id: 'periodicity-views', label: 'Piloter au quotidien', page: '/dashboard'},
  currency: {id: 'taux-de-change', label: 'Gérer les taux de change', page: '/dashboard'},
};

// Mapping back → front : un `condition_key` (chapitre back, cf. getStartOnboarding)
// → le(s) tuto(s) front correspondant(s). 1 → plusieurs pour le forecast (le back
// n'a qu'un chapitre `create_forecast`, le front enchaîne 3 tutos).
export const CONDITION_KEY_TO_TOURS: Record<string, string[]> = {
  dashboard_welcome: ['dashboard-welcome'],
  // Le back a scindé l'ancien `create_forecast` en 3 chapitres distincts.
  forecast_objectives: ['forecast-objectives'],
  forecast_formulas: ['forecast-formulas'],
  forecast_ai: ['forecast-ai'],
  categorize_transactions: ['categorization-overview'],
  invoices_overview: ['invoices-overview'],
  add_scenario: ['scenarios'],
  consolidate_entities: ['entities-consolidation-onboarding'],
  periodicity_view: ['periodicity-views'],
  exchange_rate: ['taux-de-change'],
};

// Mapping inverse : tuto front → condition_key back. Sert à retrouver, à la fin
// d'un tuto, le chapitre à marquer `completed` (via son UUID lu dans getStartOnboarding).
export const TOUR_TO_CONDITION_KEY: Record<string, string> = Object.entries(
  CONDITION_KEY_TO_TOURS,
).reduce((acc, [key, tours]) => {
  tours.forEach((t) => {
    acc[t] = key;
  });
  return acc;
}, {} as Record<string, string>);

/** Item de checklist « Prise en main », dérivé d'un chapitre back. */
export interface ChecklistItem {
  /** condition_key back (clé stable) */
  key: string;
  /** UUID back du chapitre (pour l'endpoint complete) */
  chapterId: string;
  /** tuto front à lancer (le 1er du mapping) */
  id: string;
  /** libellé affiché (FR de préférence) */
  label: string;
  /** page où lancer le tuto */
  page: string;
  /** état de complétion, lu en DB */
  completed: boolean;
  /** ordre back */
  order: number;
}

/**
 * Transforme les chapitres renvoyés par `getStartOnboarding` en items de
 * checklist « Prise en main » : libellé + `completed` (DB) + tuto à lancer.
 * Les chapitres sans mapping tuto connu sont ignorés.
 */
export function chaptersToChecklist(chapters: any[] = []): ChecklistItem[] {
  return (chapters || [])
    .map((c) => {
      const key = c?.condition_key;
      const tours = CONDITION_KEY_TO_TOURS[key];
      if (!key || !tours) return null;
      return {
        key,
        chapterId: c.id,
        id: tours[0],
        label: c.cta_fr_text || c.title || c.name || key,
        page: c.chapter_cta_open_link || '/dashboard',
        completed: c.completed === true,
        order: typeof c.order === 'number' ? c.order : 0,
      } as ChecklistItem;
    })
    .filter(Boolean)
    .sort((a, b) => (a as ChecklistItem).order - (b as ChecklistItem).order) as ChecklistItem[];
}

/** Clés back du SOCLE, dans l'ordre du parcours. On identifie le socle par ses
 *  CLÉS et non par la position : le back peut renvoyer un sous-ensemble (ex.
 *  comptes existants qui n'ont que 2 priorités, ordres 3 et 5), auquel cas un
 *  découpage positionnel prendrait des modules pour du socle. */
export const SOCLE_KEYS = [
  'dashboard_welcome',
  'forecast_objectives',
  'forecast_formulas',
  'forecast_ai',
  'categorize_transactions',
];
export const SOCLE_SIZE = SOCLE_KEYS.length;

/**
 * Steps de la checklist « Prise en main » = le SOCLE uniquement. Les autres
 * chapitres (scénario, taux de change…) sont proposés séparément en
 * « Pour aller plus loin » (cf. chaptersToModules).
 */
export function chaptersToEngineSteps(chapters: any[] = []): ChecklistItem[] {
  return chaptersToChecklist(chapters).filter((c) => SOCLE_KEYS.includes(c.key));
}


/** Icône (material-symbols) proposée par chapitre pour « Pour aller plus loin ». */
const MODULE_ICONS: Record<string, string> = {
  add_scenario: 'account_tree',
  exchange_rate: 'currency_exchange',
  invoices_overview: 'receipt_long',
  consolidate_entities: 'hub',
  periodicity_view: 'calendar_month',
};

/**
 * Copie FR de référence pour « Pour aller plus loin ». Le back renvoie des
 * libellés inégaux (titre FR mais `short_description` restée en anglais), donc
 * on affiche ce miroir quand il existe et on ne retombe sur le back que pour un
 * chapitre inconnu. Même traitement que le socle, écrit en dur côté front.
 */
const MODULE_COPY: Record<string, {label: string; desc: string}> = {
  add_scenario: {
    label: 'Créer un scénario',
    desc: 'Comparez plusieurs hypothèses sur votre trésorerie.',
  },
  exchange_rate: {
    label: 'Gérer les taux de change',
    desc: 'Suivez vos devises et leur conversion.',
  },
  invoices_overview: {
    label: 'Découvrir les échéances',
    desc: 'Visualisez vos factures à encaisser et à payer.',
  },
  consolidate_entities: {
    label: 'Consolider vos entités',
    desc: 'Réunissez plusieurs sociétés dans une vue unique.',
  },
  periodicity_view: {
    label: 'Piloter au quotidien',
    desc: 'Changez de périodicité pour affiner votre suivi.',
  },
};

/**
 * Modules « Pour aller plus loin » = les chapitres AU-DELÀ du socle, proposés
 * une fois la prise en main terminée. (Le moteur masque en plus ceux dont le
 * tuto n'est pas enregistré.)
 */
export function chaptersToModules(chapters: any[] = []): Array<{
  id: string;
  label: string;
  body: string;
  icon: string;
  page: string;
}> {
  return chaptersToChecklist(chapters)
    .filter((c) => !SOCLE_KEYS.includes(c.key))
    .map((c) => {
      const raw = (chapters || []).find((x: any) => x.id === c.chapterId);
      const copy = MODULE_COPY[c.key];
      return {
        id: c.id,
        label: copy?.label || c.label,
        body: copy?.desc || raw?.short_description || raw?.description || '',
        icon: MODULE_ICONS[c.key] || 'bolt',
        page: c.page,
      };
    });
}

/**
 * Liste finale du parcours :
 *   socle (ordre fixe) + priorités choisies (dans l'ordre de clic).
 * @param chosenPriorityKeys clés des cartes choisies, DÉJÀ dans l'ordre voulu.
 */
export function buildJourney(chosenPriorityKeys: string[] = []): JourneyStep[] {
  const seen = new Set<string>();
  const extras: JourneyStep[] = [];
  for (const key of chosenPriorityKeys) {
    const step = PRIORITY_TO_TOUR[key];
    // On ignore les clés inconnues et on dédoublonne (une priorité déjà dans le
    // socle, ou choisie deux fois, ne s'ajoute pas).
    if (step && !seen.has(step.id) && !SOCLE_STEPS.some((s) => s.id === step.id)) {
      seen.add(step.id);
      extras.push(step);
    }
  }
  return [...SOCLE_STEPS, ...extras];
}
